/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the "Elastic License
 * 2.0", the "GNU Affero General Public License v3.0 only", and the "Server Side
 * Public License v 1"; you may not use this file except in compliance with, at
 * your election, the "Elastic License 2.0", the "GNU Affero General Public
 * License v3.0 only", or the "Server Side Public License, v 1".
 */

import useEbt from '@modules/ebt/hook'

import { FeedbackSnippetIds } from './FeedbackSnippetIds'

import type { FeedbackType } from './types'

type EventType = FeedbackSnippetIds

interface FeedbackSnippetEventSchema {
  eventType: EventType
  payload: {
    feedback_type: FeedbackType
  }
}

interface UseEbtFeedbackSnippet {
  reportFeedbackSnippetEbt: (
    feedbackSnippetId: FeedbackSnippetIds,
    feedbackType: FeedbackType,
  ) => void
}

export const useEbtFeedbackSnippet = (): UseEbtFeedbackSnippet => {
  const reportEbtEvent = useEbt<FeedbackSnippetEventSchema>(
    ...Object.values(FeedbackSnippetIds).map((snippetId) => ({
      eventType: snippetId,
      schemaProps: {
        feedback_type: {
          keywordType: 'keyword' as const,
          description: 'The type of feedback provided (positive or negative)',
        },
      },
    })),
  )

  const reportFeedbackSnippetEbt: UseEbtFeedbackSnippet['reportFeedbackSnippetEbt'] = (
    feedbackSnippetId,
    feedbackType,
  ) => {
    reportEbtEvent(feedbackSnippetId, {
      feedback_type: feedbackType,
    })
  }

  return { reportFeedbackSnippetEbt }
}
